<template>
    <aside id="admin-menu"
           aria-label="Admin menu"
           class="top-0 left-0 z-50 h-dvh flex-col border-r border-base-300 bg-base-200 transition-[width]
                  duration-200 ease-out motion-reduce:transition-none"
           :class="[
               mobileOpen ? 'fixed flex w-64' : 'sticky hidden md:flex',
               !mobileOpen && (sidebarOpen ? 'w-56' : 'w-20')
           ]">
        <NuxtLink :to="{ name: 'admin' }" class="mx-auto mt-6 mb-10" aria-label="Dashboard">
            <Logo class="size-12 text-base-content/70" />
        </NuxtLink>

        <nav ref="nav" class="grow px-3">
            <ul class="flex flex-col gap-1">
                <li v-for="item in items"
                    :key="item.label"
                    class="tooltip-right"
                    :class="{ tooltip: !showLabels }"
                    :data-tip="item.label">
                    <NuxtLink :to="{ name: item.route }"
                              class="flex min-h-12 items-center gap-3 rounded-field px-3.5
                                     text-base-content/80 no-underline transition-colors hover:bg-base-300/70"
                              :class="{ 'justify-center': !showLabels }"
                              :exact-active-class="item.exact ? activeClass : undefined"
                              :active-class="item.exact ? undefined : activeClass">
                        <Icon :name="item.icon" class="size-6 shrink-0" />
                        <span :class="showLabels ? 'whitespace-nowrap' : 'sr-only'">{{ item.label }}</span>
                    </NuxtLink>
                </li>
            </ul>
        </nav>

        <button v-if="mobileOpen"
                type="button"
                class="btn mx-auto mt-4 mb-6 min-h-11 gap-2 btn-ghost"
                @click="$emit('close')">
            <Icon name="mdi:close" class="size-5" />
            Close menu
        </button>
        <button v-else
                type="button"
                class="btn mx-auto mt-4 mb-6 btn-circle btn-ghost"
                :aria-label="sidebarOpen ? 'Collapse menu' : 'Expand menu'"
                :aria-expanded="sidebarOpen"
                aria-controls="admin-menu"
                @click="$emit('toggle-sidebar')">
            <Icon name="mdi:chevron-double-right"
                  class="size-6 transition-transform duration-200 motion-reduce:transition-none"
                  :class="{ 'rotate-180': sidebarOpen }" />
        </button>
    </aside>
</template>

<script setup lang="ts">
const props = defineProps<{ mobileOpen: boolean }>();

defineEmits(['toggle-sidebar', 'close']);

const nav = ref<HTMLElement>();

watch(() => props.mobileOpen, async open => {
    if (!open) return;
    await nextTick();
    nav.value?.querySelector('a')?.focus();
});

const sidebarOpen = inject<Ref<boolean>>('sidebarOpen');
const showLabels = computed(() => props.mobileOpen || sidebarOpen?.value);
const activeClass = 'bg-primary/40 !text-base-content font-semibold';

const items = [
    { label: 'Dashboard', route: 'admin', icon: 'mdi:home-outline', exact: true },
    { label: 'Products', route: 'admin-products', exact: false, icon: 'mdi:cupcake' },
    { label: 'Galleries', route: 'admin-galleries', exact: false, icon: 'mdi:image-multiple-outline' },
    { label: 'Ingredients', route: 'admin-ingredients', exact: false, icon: 'mdi:food-variant' },
    { label: 'Packaging', route: 'admin-packagings', exact: false, icon: 'mdi:package-variant-closed' },
    { label: 'Units', route: 'admin-units', exact: false, icon: 'mdi:scale-balance' },
    { label: 'Settings', route: 'admin-settings', exact: false, icon: 'mdi:cog-outline' }
] as const;
</script>
